/**
 * Connect RPC Transport
 * Browser transport pointing at sabaody gateway with auth interceptor.
 * The baseUrl is resolved at runtime via /api/config so that the same
 * bundle works across staging and production without rebuild.
 */ import { Code, ConnectError } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { coordinatedRefresh } from '../auth';
import { getSabaodyUrl } from './runtimeConfig';
/**
 * Auth interceptor that handles Unauthenticated errors by refreshing the token
 * and retrying the request. Uses coordinatedRefresh so concurrent REST and
 * Connect failures share one in-flight refresh, and success/failure callbacks
 * (SessionWarningDialog dismiss, SessionExpiredDialog show) fire exactly once
 * inside coordinatedRefresh — this interceptor does not call them directly.
 *
 * Reactive 401 paths intentionally do NOT cancel scheduled timers — see the
 * matching note in authFetch for the reasoning.
 */ const authInterceptor = (next)=>async (req)=>{
        try {
            return await next(req);
        } catch (err) {
            if (err instanceof ConnectError && err.code === Code.Unauthenticated) {
                try {
                    const outcome = await coordinatedRefresh();
                    if (outcome.status === 'ok') {
                        return await next(req);
                    }
                // 'expired' → onRefreshFailure already fired inside coordinatedRefresh.
                // 'transient' → timers still running; surface the Unauthenticated to caller.
                // Either way, rethrow so the caller can handle the error.
                } catch  {
                // coordinatedRefresh never rejects (blanket catch in refreshAccessToken),
                // but fall through just in case.
                }
            }
            throw err;
        }
    };
/**
 * Connect transport for browser → sabaody gateway.
 * Uses JSON format for debugging; switch to binary for performance later.
 * Credentials: 'include' ensures httpOnly cookies are sent.
 *
 * Lazily created on first use so that loadRuntimeConfig() has time to
 * fetch the sabaody URL from express-backend before we lock in the baseUrl.
 */ let _transport = null;
let _binaryTransport = null;
function createTransport(useBinaryFormat) {
    return createConnectTransport({
        baseUrl: getSabaodyUrl(),
        useBinaryFormat,
        credentials: 'include',
        interceptors: [
            authInterceptor
        ]
    });
}
export function getConnectTransport() {
    if (!_transport) {
        _transport = createTransport(false);
    }
    return _transport;
}
export function getBinaryConnectTransport() {
    if (!_binaryTransport) {
        _binaryTransport = createTransport(true);
    }
    return _binaryTransport;
}
/** Lazily-initialized Connect transport (resolves sabaody URL on first use). */ export const connectTransport = new Proxy({}, {
    get (_target, prop, receiver) {
        return Reflect.get(getConnectTransport(), prop, receiver);
    }
});
export const binaryConnectTransport = new Proxy({}, {
    get (_target, prop, receiver) {
        return Reflect.get(getBinaryConnectTransport(), prop, receiver);
    }
});
